import { useState, useEffect } from "react"
import ProductCard from "../../elements/product_card"
import ProductList from "../../elements/product_list"
import { Link } from "react-router-dom"
import { BASE_URL } from "../../apiConfig"
import { toast } from "sonner"
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";

export default function ProductsPage() {
  const { token , role, roles } = useAuth();
  const [products, setProducts] = useState([])
  const [Newproducts, setNewProducts] = useState([])
  const [pinnedproducts, setPinnedProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState(role != 0 ? "list" :"grid")
  const [sortBy, setSortBy] = useState("newest")
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(30)
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category")
  const [searchTimeout, setSearchTimeout] = useState(null)

  const fetchProducts = async (page = 1, search = "") => {
    setError(null)
    try {
      const response = await fetch(BASE_URL + "/api/getproducts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category: category ? category : "",
          page: page,
          search: search, 
          sort: sortBy, 
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`API Error: ${response.status} - ${errorText}`)
        throw new Error(`Failed to fetch products: ${response.statusText}`)
      }

      const data = await response.json()

      if (data && data.data && data.data.data) {
        setProducts(data.data.data)
        setNewProducts(data.nouveau)
        setPinnedProducts(data.pinned_ids)
        setCurrentPage(data.data.current_page)
        setTotalPages(data.data.last_page)
        setTotalItems(data.data.total)
        setItemsPerPage(data.data.per_page)
      } else if (Array.isArray(data)) {
        setProducts(data)
      } else if (data && Array.isArray(data.products)) {
        setProducts(data.products)
      } else if (data && Array.isArray(data.data)) {
        setProducts(data.data)
      } else {
        console.error("API response for products is not an array:", data)
        setProducts([])
        toast.error("Received unexpected data format from products API.")
      }
    } catch (err) {
      setError(err.message)
      toast.error(err.message || "Error loading products.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts(1, searchTerm)
  }, [])

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    
    // Set new timeout for debouncing
    setSearchTimeout(
      setTimeout(() => {
        fetchProducts(1, value)
      }, 500) // 500ms delay
    )
  }

  // Handle sort change
  const handleSortChange = (value) => {
    setSortBy(value)
    setShowSortDropdown(false)
    fetchProducts(1, searchTerm) // Refetch with new sort
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      fetchProducts(page, searchTerm)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const getVisiblePages = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...")
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages)
    } else {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-white bg-teal-500"
      case "inactive":
        return "text-red-500 bg-red-100"
      case "out_of_stock":
        return "text-white bg-red-500"
      default:
        return "text-gray-500 bg-gray-100"
    }
  }

  // Skeleton Loading Components
const ProductCardSkeleton = () => (
  <div className="group bg-white rounded-3xl shadow-lg border border-gray-100 animate-pulse">
    {/* Image Skeleton */}
    <div className="relative overflow-hidden rounded-t-3xl">
      <div className="aspect-square bg-gray-200 rounded-t-3xl"></div>
      <div className="absolute top-3 right-3 flex gap-1">
        <div className="bg-gray-300 w-16 h-5 rounded-full"></div>
        <div className="bg-gray-300 w-12 h-5 rounded-full"></div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="px-4 pt-2 pb-4 max-lg:pb-3">
      <div className="flex justify-between items-center mb-2">
        <div className="bg-gray-200 w-20 h-4 rounded"></div>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-200 w-4 h-4 rounded"></div>
          ))}
        </div>
      </div>

      <div className="bg-gray-200 w-3/4 h-5 rounded mb-2"></div>
      <div className="bg-gray-200 w-full h-4 rounded mb-4"></div>

      {/* Pricing Skeleton */}
      <div className="mb-4 max-lg:mb-1.5">
        <div className="bg-gray-200 w-20 h-6 rounded mb-1"></div>
        <div className="bg-gray-200 w-32 h-4 rounded"></div>
      </div>

      {/* Action Buttons Skeleton */}
      <div className="flex space-x-2">
        <div className="flex-1 bg-gray-200 h-10 rounded-xl"></div>
        <div className="bg-gray-200 w-10 h-10 rounded-xl"></div>
      </div>
    </div>
  </div>
)

const ProductListSkeleton = ({ role }) => (
  <div className="transition-all duration-300 border-b border-gray-100 animate-pulse">
                <div className={`w-full min-w-0 grid ${role !== 0 ? 'grid-cols-11 max-xl:grid-cols-9' : 'grid-cols-9 max-xl:grid-cols-7'} gap-4 items-center py-1 px-6 max-xl:px-3`}>
      
      {/* Product Info */}
      <div className="col-span-4 max-xl:col-span-2 flex items-center gap-4">
        <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
        <div className="flex-1 space-y-2">
          <div className="bg-gray-200 w-32 h-4 rounded"></div>
          <div className="bg-gray-200 w-24 h-3 rounded"></div>
        </div>
      </div>
      
      {/* Status */}
      <div className="col-span-1 space-y-1">
        <div className="bg-gray-200 w-16 h-5 rounded-full"></div>
        <div className="bg-gray-200 w-12 h-4 rounded-full"></div>
      </div>
      
      {/* Rating */}
      <div className="col-span-1">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-gray-200 w-4 h-4 rounded"></div>
          ))}
        </div>
      </div>

      {/* Stock & Prices */}
      {role !== 0 && (
        <>
          <div className="col-span-1">
            <div className="bg-gray-200 w-8 h-4 rounded mx-auto"></div>
          </div>
          <div className="col-span-1">
            <div className="bg-gray-200 w-16 h-4 rounded"></div>
          </div>
        </>
      )}
      
      <div className="col-span-1">
        <div className="bg-gray-200 w-16 h-4 rounded"></div>
      </div>
      <div className="col-span-1">
        <div className="bg-gray-200 w-16 h-4 rounded"></div>
      </div>
      
      {/* Actions */}
      <div className="col-span-1 flex justify-center gap-1">
        {[...Array(role !== 0 ? 4 : 2)].map((_, i) => (
          <div key={i} className="bg-gray-200 w-8 h-8 rounded-xl"></div>
        ))}
      </div>
    </div>
  </div>
)


  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-1.5 py-12">
        <div className="flex mx-2.5 flex-col md:flex-row justify-between items-start md:items-center mb-5 max">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              Products
            </h1>
            <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
              Efficiently manage your product catalog with robust search, sorting, and display options.
            </p>
          </div>
          <div className="mt-6 md:mt-0">
            {role === 1 || (roles?.add_product === 1 && role === 2) ? (
              <Link to="/products/create">
                <button className="cursor-pointer text-xs max-xl:py-3 bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white font-semibold max-xl:py-2 max-xl:px-4 py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                  <span className="flex items-center">
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Product
                  </span>
                </button>
              </Link>
            ) : (
              ""
            )}
          </div>
        </div>

        {/* Search and Controls */}
        <div className="bg-white mx-2.5 rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            {/* Search Bar */}
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Controls */}
            <div className="flex gap-4 max-xl:w-full">
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span className="text-xs">Sort By</span>
                  <svg
                    className={`h-3 w-3 text-gray-500 transition-transform ${showSortDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSortDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-2 text-xs ">
                      {[
                        { value: "name", label: "Alphabetical" },
                        { value: "price", label: "Price" },
                        { value: "stock", label: "Stock" },
                        { value: "newest", label: "Newest First" },
                        { value: "oldest", label: "Oldest First" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full cursor-pointer text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)] font-medium" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "grid"
                      ? "bg-[var(--color-primary-500)] text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-[var(--color-primary-500)] text-white"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && (
        <div className="">
          {/* Grid View Skeleton */}
          {viewMode === "grid" ? (
            <div className="grid mt-20 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-lg:gap-1.5 max-lg:gap-y-5">
              {[...Array(8)].map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            /* List View Skeleton */
            <div className="space-y-2 bg-white rounded-xl shadow-sm">
              <div className="overflow-x-scroll custom-scrollbar">
                {/* Table Header (hidden on small screens) */}
                <div className={`hidden md:grid w-full min-w-0 ${role !== 0 ? 'md:grid-cols-11 max-xl:grid-cols-9' : 'md:grid-cols-9 max-xl:grid-cols-7'} gap-4 py-3 px-6 max-xl:px-3 bg-gray-50 rounded-t-lg border-b border-gray-200 mb-2`}>
                  {[...Array(role !== 0 ? 11 : 9)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-4 rounded"></div>
                  ))}
                </div>
                {/* Table Rows */}
                {[...Array(5)].map((_, i) => (
                  <ProductListSkeleton key={i} role={role} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {/* Results Count */}
            <div className="mb-8 px-2.5 flex justify-between items-center">
              <span className="inline-flex items-center px-4 py-2 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-[10px] font-medium">
                Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} products
              </span>
              {totalPages > 1 && (
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>

            {products.length > 0 ? (
              <>
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:px-4 gap-4 max-lg:gap-1.5 max-lg:gap-y-5">
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        getStatusColor={getStatusColor}
                        fetchProducts={() => fetchProducts(currentPage, searchTerm)}
                        newProducts={Newproducts}
                        pinnedproducts={pinnedproducts}
                      />
                    ))}
                  </div>
                ) : (
                
                  <div className="space-y-2 mx-2.5 bg-white rounded-xl shadow-sm">
                    <div className="overflow-x-auto custom-scrollbar">
                      {/* Wrapper to enable horizontal scroll on small screens */}
                      <div className="min-w-[800px]">
                        <div
                          className={`grid w-full ${role !== 0 ? "grid-cols-11" : "grid-cols-9"} gap-2 sm:gap-3 lg:gap-4 py-3 px-3 sm:px-4 lg:px-6 bg-gray-50 rounded-t-lg border-b border-gray-200 mb-2 font-semibold text-gray-700 text-[10px] sm:text-xs`}
                        >
                          <div className="col-span-4 md:col-span-3">Product</div>
                          <div className="col-span-1">Status</div>
                          <div className="col-span-1">Rating</div>
                          {role !== 0 && (
                            <>
                              <div className="col-span-1 text-center">Stock</div>
                              <div className="col-span-1">Prix d'achat</div>
                            </>
                          )}
                          <div className="col-span-1">Prix de vente</div>
                          <div className="col-span-1">Prix suggéré</div>
                          <div className="col-span-1 text-center">Actions</div>
                        </div>
                        {products.map((product) => (
                          <ProductList
                            role={role}
                            key={product.id}
                            product={product}
                            getStatusColor={getStatusColor}
                            fetchProducts={() => fetchProducts(currentPage, searchTerm)}
                            newProducts={Newproducts}
                            pinnedproducts={pinnedproducts}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="flex items-center space-x-2">
                      {/* Previous Button */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-2 py-1.5 cursor-pointer rounded-lg text-sm font-medium transition-all duration-200 ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                        }`}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {/* Page Numbers */}
                      {getVisiblePages().map((page, index) => (
                        <button
                          key={index}
                          onClick={() => (typeof page === "number" ? handlePageChange(page) : null)}
                          disabled={typeof page !== "number"}
                          className={`px-3 py-1 cursor-pointer rounded-lg text-sm font-medium transition-all duration-200 ${
                            page === currentPage
                              ? "bg-[var(--color-primary-500)] text-white"
                              : typeof page === "number"
                                ? "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                                : "text-gray-400 cursor-default"
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      {/* Next Button */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-2 py-1.5 cursor-pointer rounded-lg text-sm font-medium transition-all duration-200 ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-[var(--color-primary-500)] hover:text-white border border-gray-200"
                        }`}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-10">
                <div className="w-28 h-28 mx-auto mb-8 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-12 w-12 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">No products found</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Try adjusting your search terms or filters to find what you're looking for
                </p>
                {role === 1 || (roles?.add_product === 1 && role === 2) ? (
                  <Link href="/products/create">
                    <button className="bg-[var(--color-primary-500)] cursor-pointer text-sm hover:bg-[var(--color-primary-400)] text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300">
                      Create Your First Product
                    </button>
                  </Link>
                ) : (
                  ""
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
